import { Fragment, useMemo } from "react";
import type { ReactNode } from "react";
import { toJsxRuntime } from "hast-util-to-jsx-runtime";
import type { Jsx } from "hast-util-to-jsx-runtime";
import { jsx, jsxs } from "react/jsx-runtime";

import { languageForFile, lowlight, MAX_HIGHLIGHT_CHARS } from "./highlighting";

/**
 * Syntax-highlighted read-only code block for the Studio code pane.
 *
 * Highlighting renders through hast → JSX — no `dangerouslySetInnerHTML`.
 * Unknown extensions and oversized sources fall back to the raw string:
 * highlighting is additive, never lossy.
 */
interface HighlightedCodeProps {
  /** Raw source text, rendered verbatim when highlighting is unavailable. */
  code: string;
  /** File path/name whose extension picks the grammar. */
  file: string;
}

export function HighlightedCode({ code, file }: HighlightedCodeProps) {
  const highlighted = useMemo<ReactNode | null>(() => {
    const language = languageForFile(file);
    if (language === null || code.length > MAX_HIGHLIGHT_CHARS) return null;
    try {
      return toJsxRuntime(lowlight.highlight(language, code), {
        Fragment,
        jsx: jsx as Jsx,
        jsxs: jsxs as Jsx,
      });
    } catch {
      // A grammar throwing on hostile input must never blank the pane.
      return null;
    }
  }, [code, file]);

  return <code className="hljs">{highlighted ?? code}</code>;
}
